import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Card, CardContent } from './ui/card';
import { Loader2, CreditCard, Calendar, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentModal = ({ isOpen, onClose, onSuccess, initialAccommodation = 'condo' }) => {
  const [paymentPlan, setPaymentPlan] = useState('full');
  const [accommodationType, setAccommodationType] = useState(initialAccommodation);
  const [guestCount, setGuestCount] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAccommodationType(initialAccommodation);
      setGuestCount(1);
      setError('');
    }
  }, [isOpen, initialAccommodation]);

  useEffect(() => {
    const maxGuests = accommodationType === 'condo' ? 6 : 3;
    if (guestCount > maxGuests) {
      setGuestCount(maxGuests);
    }
  }, [accommodationType, guestCount]);

  // Server-side pricing (matching backend)
  const pricing = {
    condo: 2500, // $2500 per person
    villa: 3000  // $3000 per person
  };

  const pricePerPerson = pricing[accommodationType];
  const totalPrice = pricePerPerson * guestCount;
  const depositPerPerson = pricePerPerson * 0.20; // 20%
  const totalDeposit = depositPerPerson * guestCount;

  const calculateTotal = () => {
    if (paymentPlan === 'full') {
      return totalDeposit;
    }
    return totalDeposit; // Same for display, but installment divides by 3
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      setError('Please fill in all fields');
      return false;
    }
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email');
      return false;
    }
    if (formData.phone.length < 10) {
      setError('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const handlePayment = async () => {
    setError('');
    
    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    try {
      const originUrl = window.location.origin;
      const referralCode = localStorage.getItem('playcation_referral_code');

      const response = await axios.post(`${API}/payments/checkout`, {
        currency: 'usd',
        guest_count: guestCount,
        payment_plan: paymentPlan,
        accommodation_type: accommodationType,
        customer_info: formData,
        origin_url: originUrl,
        referral_code: referralCode || undefined
      });

      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Payment error:', err);
      const responseData = err.response?.data;
      const providerMessage = responseData && typeof responseData === 'object' ? responseData.detail : null;
      setError(
        typeof providerMessage === 'string' && !providerMessage.includes('<')
          ? providerMessage
          : "We couldn't start secure checkout. Please try again or text King at 832-596-5177."
      );
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="payment-modal max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="stripe-payment-modal">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-center">
            Reserve Your Spot
          </DialogTitle>
          <DialogDescription className="text-center text-[#B5A7C3]">
            Choose your stay, guest count, and secure payment option.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Accommodation Type Selection */}
          <div>
            <Label className="text-lg font-semibold mb-3 block">
              Choose Your Accommodation
            </Label>
            <RadioGroup value={accommodationType} onValueChange={setAccommodationType}>
              <Card className={`payment-option-card mb-3 ${accommodationType === 'condo' ? 'selected' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="condo" id="condo" className="mt-1" data-testid="accommodation-condo-radio" />
                    <Label htmlFor="condo" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-lg">Condo</span>
                        <span className="text-2xl font-bold text-purple-700">
                          ${pricing.condo}/person
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        6 condos available • <span className="font-semibold text-purple-700">Sleeps 1-6 guests per condo</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        Includes: Breakfast, Lunch, Dinner & Most Liquor
                      </p>
                    </Label>
                  </div>
                </CardContent>
              </Card>

              <Card className={`payment-option-card ${accommodationType === 'villa' ? 'selected' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="villa" id="villa" className="mt-1" data-testid="accommodation-villa-radio" />
                    <Label htmlFor="villa" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-lg">Main Villa</span>
                        <span className="text-2xl font-bold text-purple-700">
                          ${pricing.villa}/person
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        9 rooms available • <span className="font-semibold text-purple-700">Sleeps 1-3 guests per room</span> • Main party location
                      </p>
                      <p className="text-xs text-gray-500">
                        Includes: Breakfast, Lunch, Dinner & Most Liquor
                      </p>
                    </Label>
                  </div>
                </CardContent>
              </Card>
            </RadioGroup>
          </div>

          {/* Guest Count */}
          <div>
            <Label htmlFor="guestCount" className="text-lg font-semibold mb-3 block">
              How many people in your {accommodationType === 'condo' ? 'condo' : 'room'}?
            </Label>
            <div className="guest-count-panel">
              <div className="guest-count-control">
                <button
                  type="button"
                  aria-label="Decrease guest count"
                  data-testid="guest-count-decrease-button"
                  onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                  className="guest-count-button"
                >
                  −
                </button>
                <div className="guest-count-readout">
                  <Input
                    id="guestCount"
                    data-testid={accommodationType === 'condo' ? 'guest-select-condo' : 'guest-select-villa'}
                    type="number"
                    min="1"
                    max={accommodationType === 'condo' ? '6' : '3'}
                    value={guestCount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setGuestCount(1);
                        return;
                      }
                      const numValue = parseInt(value);
                      if (!isNaN(numValue)) {
                        const max = accommodationType === 'condo' ? 6 : 3;
                        if (numValue >= 1 && numValue <= max) {
                          setGuestCount(numValue);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseInt(e.target.value);
                      if (isNaN(value) || value < 1) {
                        setGuestCount(1);
                      }
                    }}
                    className="guest-count-input"
                    aria-label="Guest count"
                  />
                  <span>{guestCount === 1 ? 'guest' : 'guests'}</span>
                </div>
                <button
                  type="button"
                  aria-label="Increase guest count"
                  data-testid="guest-count-increase-button"
                  onClick={() => {
                    const max = accommodationType === 'condo' ? 6 : 3;
                    setGuestCount(Math.min(max, guestCount + 1));
                  }}
                  className="guest-count-button"
                >
                  +
                </button>
              </div>
              <div className="guest-count-presets" aria-label="Quick guest count choices">
                {Array.from({ length: accommodationType === 'condo' ? 6 : 3 }, (_, index) => index + 1).map((count) => (
                  <button
                    type="button"
                    key={count}
                    data-testid={`guest-preset-${count}`}
                    className={guestCount === count ? 'active' : ''}
                    onClick={() => setGuestCount(count)}
                  >
                    {count}
                  </button>
                ))}
              </div>
              <p className="guest-count-help">
                {accommodationType === 'condo'
                  ? 'Choose 1-6 guests for your condo'
                  : 'Choose 1-3 guests for your villa room'
                }
              </p>
              <div className="guest-total-panel">
                <p>
                  Total for {guestCount} {guestCount === 1 ? 'guest' : 'guests'}: ${totalPrice.toLocaleString()}
                </p>
                <span>
                  ${pricePerPerson.toLocaleString()} per person × {guestCount} {guestCount === 1 ? 'guest' : 'guests'}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Plan Selection */}
          <div>
            <Label className="text-lg font-semibold mb-3 block">
              Payment Option
            </Label>
            <RadioGroup value={paymentPlan} onValueChange={setPaymentPlan}>
              <Card className={`payment-option-card mb-3 ${paymentPlan === 'full' ? 'selected' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="full" id="full" className="mt-1" data-testid="payment-full-radio" />
                    <Label htmlFor="full" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-lg">Pay Deposit Now</span>
                        <span className="text-2xl font-bold text-purple-700">
                          ${(depositPerPerson * guestCount).toFixed(2)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        20% non-refundable deposit. Balance due 30 days before departure.
                      </p>
                    </Label>
                  </div>
                </CardContent>
              </Card>

              <Card className={`payment-option-card ${paymentPlan === 'installment' ? 'selected' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="installment" id="installment" className="mt-1" data-testid="payment-installment-radio" />
                    <Label htmlFor="installment" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-lg">3-Month Payment Plan</span>
                        <span className="text-2xl font-bold text-purple-700">
                          ${(depositPerPerson * guestCount / 3).toFixed(2)}/mo
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Split deposit into 3 easy monthly payments. First payment today.
                      </p>
                    </Label>
                  </div>
                </CardContent>
              </Card>
            </RadioGroup>
          </div>

          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Your Information</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  data-testid="payment-first-name-input"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="John"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  data-testid="payment-last-name-input"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                data-testid="payment-email-input"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john.doe@example.com"
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                data-testid="payment-phone-input"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          {/* Payment Summary */}
          <Card className="payment-summary-card" data-testid="payment-summary-card">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold mb-4">Payment Summary</h3>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Accommodation:</span>
                  <span className="font-semibold">{accommodationType === 'condo' ? 'Condo' : 'Main Villa'}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Price per person:</span>
                  <span>${pricePerPerson.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Number of guests:</span>
                  <span>{guestCount}</span>
                </div>
                <div className="flex justify-between font-semibold text-lg text-gray-700 pt-2 border-t">
                  <span>Total package price:</span>
                  <span>${totalPrice.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-xl text-purple-700 pt-2 border-t-2 border-purple-200">
                  <span>20% Deposit due now:</span>
                  <span>${totalDeposit.toLocaleString()}</span>
                </div>
                {paymentPlan === 'installment' && (
                  <div className="flex justify-between text-gray-600 bg-blue-50 p-2 rounded">
                    <span>Monthly payments (3 months):</span>
                    <span className="font-semibold">${(totalDeposit / 3).toFixed(2)}/month</span>
                  </div>
                )}
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold">
                    {paymentPlan === 'installment' ? 'First Payment:' : 'Total Due Today:'}
                  </span>
                  <span className="text-3xl font-bold text-purple-700">
                    ${paymentPlan === 'installment' ? (calculateTotal() / 3).toFixed(2) : calculateTotal().toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" data-testid="payment-error-alert">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button
              variant="outline"
              data-testid="payment-cancel-button"
              onClick={onClose}
              className="flex-1"
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              data-testid="proceed-payment-button"
              disabled={isProcessing}
              className="flex-1 cta-primary text-lg py-6"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Proceed to Payment
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-center text-gray-500">
            Secure payment powered by Stripe. Your information is encrypted and secure.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
